/**
 * Static catalog of every endpoint exposed by the TransPay backend services.
 *
 * The docs page filters this catalog by the viewer's role + permission set,
 * so a merchant user only sees endpoints they can actually call. This is a
 * documentation aid only — the server is the source of truth for authorization.
 *
 * Maintenance: when a new route is added, add a corresponding entry here so
 * the docs page picks it up. Permission keys must match those granted in the
 * RBAC seed (packages/shared-schema/src/seedData.ts).
 */

export type ApiHttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';

export type ApiScope =
  /** Server-to-server, authenticated via merchant API credentials. */
  | 'merchant-api'
  /** Dashboard endpoints, authenticated via session cookie / Bearer JWT. */
  | 'dashboard';

export type ApiUserType =
  | 'any'
  | 'internal-only'
  | 'external-only';

export type ApiRequestHeader = {
  name: string;
  required: boolean;
  description: string;
  example?: string;
};

export type ApiResponseHeader = {
  name: string;
  description: string;
};

export type ApiErrorCode = {
  code: string;
  httpStatus: number;
  description: string;
};

export type ApiResponseField = {
  path: string;
  type: string;
  description: string;
};

export type ApiPaymentMethod = {
  category: string;
  code: string;
  name: string;
};

export type ApiEndpoint = {
  method: ApiHttpMethod;
  path: string;
  title: string;
  description: string;
  category: string;
  scope: ApiScope;
  /**
   * Permission keys required to call this endpoint. ANY one of them grants
   * access. Empty array = no permission needed beyond authentication.
   */
  permissions: string[];
  userType: ApiUserType;
  /** Base URL hint shown in the docs (omit to fall back to the in-app origin). */
  baseUrl?: string;
  /** Required + optional request headers — surfaced as a table in the docs. */
  requestHeaders?: ApiRequestHeader[];
  /** Notable response headers (typically content-type, x-request-id, rate-limit). */
  responseHeaders?: ApiResponseHeader[];
  /** Documented error codes with HTTP status + cause. Shown as a table. */
  errorCodes?: ApiErrorCode[];
  /** Field-level annotations for the response shape. */
  responseFields?: ApiResponseField[];
  /** Supported payment-method codes — rendered as a grouped table in the docs. */
  paymentMethods?: ApiPaymentMethod[];
  /** Example curl for the docs page. */
  curlExample?: string;
  requestBodyExample?: unknown;
  responseExample?: unknown;
  /** Free-text caveats (rate limits, scoping rules, etc.). */
  notes?: string;
};

export const API_ENDPOINTS: readonly ApiEndpoint[] = [
  // ====== Authentication ======
  {
    method: 'POST',
    path: '/api/auth/login',
    title: 'Login',
    description: 'Authenticate with email + password. Returns a session JWT and sets the HttpOnly session cookie. IP whitelist is enforced — login fails if the request IP is not in the user\'s personal whitelist.',
    category: 'Authentication',
    scope: 'dashboard',
    permissions: [],
    userType: 'any',
    requestBodyExample: { email: 'user@example.com', password: '••••••••', captcha: 'TRANSPAY' },
    responseExample: { token: '<jwt>', profile: { id: 2, email: 'user@example.com', userType: 'external', merchantId: '63bc67dc-…', isSuperuser: false } },
  },
  {
    method: 'GET',
    path: '/api/auth/me',
    title: 'Current Session',
    description: 'Returns the current viewer\'s profile, role assignment, and effective permission set. The frontend uses this to gate menus and routes.',
    category: 'Authentication',
    scope: 'dashboard',
    permissions: [],
    userType: 'any',
    responseExample: {
      id: 2,
      email: 'user@example.com',
      userType: 'external',
      isSuperuser: false,
      role: { id: 5, slug: 'merchant_admin', name: 'Merchant Admin', scope: 'merchant' },
      merchant: { id: '63bc67dc-…', name: 'DFBET' },
      permissions: ['overview.access', 'deposits.list.view', '…'],
    },
  },
  {
    method: 'POST',
    path: '/api/auth/logout',
    title: 'Logout',
    description: 'Invalidate the current session (denylists the JTI) and clear the cookie.',
    category: 'Authentication',
    scope: 'dashboard',
    permissions: [],
    userType: 'any',
  },
  {
    method: 'POST',
    path: '/api/auth/refresh',
    title: 'Refresh Session',
    description: 'Rotate the session token when within 1 hour of expiry. Older tokens are denylisted.',
    category: 'Authentication',
    scope: 'dashboard',
    permissions: [],
    userType: 'any',
  },
  {
    method: 'POST',
    path: '/api/account/credentials',
    title: 'Reveal Merchant API Credentials',
    description: 'Re-authenticates with password, then returns the merchant\'s client ID + token suffix. The full token is shown only once at create/rotate time — this endpoint only confirms the prefix.',
    category: 'Authentication',
    scope: 'dashboard',
    permissions: ['account.credentials.view'],
    userType: 'external-only',
  },
  {
    method: 'GET',
    path: '/api/auth/invite/:token',
    title: 'Lookup Invite (Public)',
    description: 'Public endpoint — used by the /accept-invite page to display invite metadata before the user fills in the acceptance form.',
    category: 'Authentication',
    scope: 'dashboard',
    permissions: [],
    userType: 'any',
  },
  {
    method: 'POST',
    path: '/api/auth/invite/accept',
    title: 'Accept Invite (Public)',
    description: 'Consume a one-time invite token, create the user, auto-log them in. Auto-whitelists the accepting IP so the deny-all model doesn\'t lock the new user out.',
    category: 'Authentication',
    scope: 'dashboard',
    permissions: [],
    userType: 'any',
    requestBodyExample: { token: '<invite-token>', password: '••••••••', accountName: 'John Doe' },
  },

  // ====== Server-to-Server (Merchant API) ======
  {
    method: 'POST',
    path: '/api/v1/cashin',
    title: 'Create Deposit',
    description: 'Server-to-server endpoint to create a deposit transaction. Returns the QR/payment info you display to the end customer. The trx is created with status `pending`; once the customer pays it enters `pendingBalance`, then settles to `activeBalance` at the scheduled `settleAt` (by default: paid 00:00–11:59 GMT+7 → same day 19:00; paid 12:00–23:59 → next day 13:00). See the Payment Methods table below for the supported `paymentMethodCode` values. The accepted set is the platform-enabled methods intersected with your merchant allow-list (managed by TransPay internal staff); a non-QRIS method returns a `paymentInfo.payUrl` to open instead of a QR.',
    category: 'Server-to-Server (Merchant API)',
    scope: 'merchant-api',
    permissions: [],
    userType: 'external-only',
    baseUrl: 'https://api.trans-pay.app',
    requestHeaders: [
      {
        name: 'content-type',
        required: true,
        description: 'Must be application/json.',
        example: 'application/json',
      },
      {
        name: 'x-transpay-client-id',
        required: true,
        description: '32-character client ID issued at merchant onboarding.',
        example: 'Fm1p2iPUENI0sEFuBwpCj9Gz0yZAKjVF',
      },
      {
        name: 'x-transpay-client-token',
        required: true,
        description: '48-character client token paired with the client ID. Treat as a secret.',
        example: '<48-char token>',
      },
      {
        name: 'idempotency-key',
        required: false,
        description: 'Optional client-supplied key. Same key + same body returns the same response without creating a duplicate transaction. Recommended.',
        example: '01HXYZ...',
      },
    ],
    responseHeaders: [
      { name: 'content-type', description: 'application/json; charset=utf-8' },
      { name: 'x-request-id', description: 'Server-generated request id for log correlation.' },
      { name: 'x-ratelimit-limit', description: 'Requests allowed per window.' },
      { name: 'x-ratelimit-remaining', description: 'Requests left in the current window.' },
    ],
    errorCodes: [
      { code: 'invalid_credentials', httpStatus: 401, description: 'client-id or client-token missing/wrong.' },
      { code: 'invalid_amount', httpStatus: 400, description: 'amount or other body field failed validation. The `detail` envelope carries the per-field reason.' },
      { code: 'amount_below_minimum', httpStatus: 400, description: 'amount is below the merchant\'s configured cashin minimum.' },
      { code: 'amount_exceeds_limit', httpStatus: 400, description: 'amount is above the merchant\'s configured cashin maximum.' },
      { code: 'method_disabled', httpStatus: 400, description: 'paymentMethodCode is not enabled platform-wide by the operator.' },
      { code: 'method_not_enabled_for_merchant', httpStatus: 400, description: 'paymentMethodCode is not enabled for your merchant. The per-merchant allow-list is controlled by TransPay internal staff — contact support to enable a method.' },
      { code: 'api_disabled', httpStatus: 403, description: 'Merchant API access has been disabled.' },
      { code: 'merchant_suspended', httpStatus: 403, description: 'Merchant is suspended; contact TransPay support.' },
      { code: 'ip_whitelist_required', httpStatus: 403, description: 'The merchant has no API IP whitelist entries — at least one is required.' },
      { code: 'ip_not_whitelisted', httpStatus: 403, description: 'The request source IP is not on the merchant\'s API IP whitelist.' },
      { code: 'idempotency_conflict', httpStatus: 409, description: 'Same idempotency-key used with a different request body.' },
      { code: 'duplicate_merchant_ref', httpStatus: 409, description: '`referenceId` was already used by your merchant (any status, cash-in or cash-out). Use a new value.' },
      { code: 'reference_id_unavailable', httpStatus: 409, description: '`referenceId` is already in use as a provider order id platform-wide. Use a more specific value (e.g. prefix it with your own merchant code).' },
      { code: 'persist_failed', httpStatus: 500, description: 'Internal DB write failed. Safe to retry with the same idempotency-key.' },
      { code: 'provider_not_configured', httpStatus: 503, description: 'Payment provider integration not configured for this merchant. Contact TransPay support.' },
      { code: 'merchant_lookup_failed', httpStatus: 503, description: 'Internal lookup transient failure. Safe to retry.' },
      { code: 'upstream_failed', httpStatus: 502, description: 'Payment provider rejected or did not respond. The `detail` envelope carries an opaque provider code.' },
    ],
    responseFields: [
      { path: 'data.trxId', type: 'string (uuid)', description: 'TransPay transaction id. Persist this on your side as the canonical id.' },
      { path: 'data.referenceId', type: 'string', description: 'Echo of your `referenceId` in the request (auto-generated `REF-…` when omitted). This exact value is also forwarded to the payment provider as the order id, so it must be unique — 1 … 64 characters.' },
      { path: 'data.trxStatus', type: '"pending" | "paid" | "failed"', description: 'Initial status is always `pending`. Webhook will update.' },
      { path: 'data.amount', type: 'integer (IDR)', description: 'Echo of the requested amount.' },
      { path: 'data.fee', type: 'integer (IDR)', description: 'TransPay fee deducted from amount.' },
      { path: 'data.merchantRevenue', type: 'integer (IDR)', description: '`amount - fee` — net credited to your wallet on settlement.' },
      { path: 'paymentInfo.type', type: '"code" | "url" | "html" | "json"', description: 'How to present the payment to the customer — this DIFFERS by method. `code` = render a QR / show a number (QRIS, StaticQRIS, and the *VA virtual-account methods). `url` = redirect/open a hosted page (e-wallets DANAWALLET, OVOWALLET, SHOPEEPAY-APP/WEB, LINKAJA-APP/WEB, and ConvenienceStore). `html`/`json` = embedded instructions to render/parse.' },
      { path: 'paymentInfo.content', type: 'string', description: 'The raw payment instruction, interpreted per `type`: QRIS/StaticQRIS → QR payload; *VA → virtual-account number; e-wallet/OTC → redirect URL.' },
      { path: 'paymentInfo.qrCode', type: 'string | null', description: 'Convenience alias — QR payload for QRIS/StaticQRIS (`type="code"`). `null` for Virtual Account methods (a VA number is not a QR — read the VA number from `content`). Empty string for url-type methods.' },
      { path: 'paymentInfo.payUrl', type: 'string (url)', description: 'Convenience alias — filled when `type="url"`. Open in the customer browser/app (e-wallet redirect / OTC voucher page). Empty string for QRIS/VA.' },
      { path: 'paymentInfo.expiredTime', type: 'string (YYYYMMDDhhmmss, GMT+7)', description: 'When the payment instrument expires.' },
    ],
    paymentMethods: [
      { category: 'QR', code: 'QRIS', name: 'QRIS (dynamic)' },
      { category: 'QR', code: 'StaticQRIS', name: 'QRIS (static)' },
      { category: 'Virtual Account', code: 'BNIVA', name: 'BNI' },
      { category: 'Virtual Account', code: 'BCAVA', name: 'BCA' },
      { category: 'Virtual Account', code: 'BRIVA', name: 'BRI' },
      { category: 'Virtual Account', code: 'BSIVA', name: 'BSI' },
      { category: 'Virtual Account', code: 'CIMBVA', name: 'CIMB Niaga' },
      { category: 'Virtual Account', code: 'MandiriVA', name: 'Mandiri' },
      { category: 'Virtual Account', code: 'PermataVA', name: 'Permata' },
      { category: 'Virtual Account', code: 'DanamonVA', name: 'Danamon' },
      { category: 'Virtual Account', code: 'BNCVA', name: 'BNC' },
      { category: 'Virtual Account', code: 'INAVA', name: 'INA' },
      { category: 'Virtual Account', code: 'BNISyariahVA', name: 'BNI Syariah' },
      { category: 'Virtual Account', code: 'ALTOVA', name: 'ALTO network' },
      { category: 'Virtual Account', code: 'SinarmasVA', name: 'Sinarmas' },
      { category: 'Virtual Account', code: 'PrimaVA', name: 'Prima network' },
      { category: 'Virtual Account', code: 'MUAMALATVA', name: 'Muamalat' },
      { category: 'Virtual Account', code: 'NobuVA', name: 'Nobu' },
      { category: 'E-wallet', code: 'DANAWALLET', name: 'DANA' },
      { category: 'E-wallet', code: 'OVOWALLET', name: 'OVO' },
      { category: 'E-wallet', code: 'SHOPEEPAY-APP', name: 'ShopeePay (app)' },
      { category: 'E-wallet', code: 'SHOPEEPAY-WEB', name: 'ShopeePay (web)' },
      { category: 'E-wallet', code: 'LINKAJA-APP', name: 'LinkAja (app)' },
      { category: 'E-wallet', code: 'LINKAJA-WEB', name: 'LinkAja (web)' },
      { category: 'Over-the-counter', code: 'ConvenienceStore', name: 'Convenience store (e.g. Alfamart)' },
    ],
    curlExample: `curl -X POST "https://api.trans-pay.app/api/v1/cashin" \\
  -H "content-type: application/json" \\
  -H "x-transpay-client-id: <32-char client id>" \\
  -H "x-transpay-client-token: <48-char token>" \\
  -H "idempotency-key: <your-unique-key>" \\
  -d '{
    "amount": 10000,
    "referenceId": "MERCHANT-TRX-001",
    "paymentMethodCode": "QRIS",
    "customer": { "name": "John Doe", "email": "john@example.com" }
  }'`,
    requestBodyExample: {
      amount: 10000,
      referenceId: 'MERCHANT-TRX-001',
      remark: 'Top up wallet',
      paymentMethodCode: 'QRIS',
      customer: {
        name: 'John Doe',
        email: 'john@example.com',
        phone: '081234567890',
      },
    },
    responseExample: {
      data: {
        trxId: 'a5ecfddb-…',
        referenceId: 'MERCHANT-TRX-001',
        trxStatus: 'pending',
        amount: 10000,
        fee: 250,
        merchantRevenue: 9750,
      },
      paymentInfo: {
        qrCode: '00020101021226670016COM.NOBUBANK.WWW…',
        payUrl: 'https://id-payer.example/…',
        expiredTime: '20260529221500',
      },
    },
    notes:
      'Required: `x-transpay-client-id` + `x-transpay-client-token` headers. Request source IP must be on your API IP whitelist (configurable in dashboard → Merchant detail → IP Whitelist). Provide `idempotency-key` to make retries safe.',
  },
  {
    method: 'GET',
    path: '/api/v1/cashin/:idOrRef',
    title: 'Check Deposit Status',
    description:
      'Server-to-server status check for a deposit transaction. Accepts EITHER the TransPay `trxId` (UUID v4) OR your own `referenceId` as the path param — shape-detected. Use this as a webhook fallback when your endpoint was unreachable, or to reconcile pending transactions in your own system. Rate-limited 60 requests/minute per IP (≈ 1 poll/sec).',
    category: 'Server-to-Server (Merchant API)',
    scope: 'merchant-api',
    permissions: [],
    userType: 'external-only',
    baseUrl: 'https://api.trans-pay.app',
    requestHeaders: [
      { name: 'x-transpay-client-id', required: true, description: '32-char merchant client id.', example: 'Fm1p2iPUENI0sEFuBwpCj9Gz0yZAKjVF' },
      { name: 'x-transpay-client-token', required: true, description: '48-char merchant client token.', example: '<48-char token>' },
    ],
    errorCodes: [
      { code: 'invalid_credentials', httpStatus: 401, description: 'Headers missing or wrong length.' },
      { code: 'merchant_suspended', httpStatus: 403, description: 'Merchant has been suspended.' },
      { code: 'trx_not_found', httpStatus: 404, description: 'No deposit found for this `idOrRef` under your merchant. Same response for "exists but belongs to another merchant" — we never leak cross-merchant existence.' },
      { code: 'merchant_lookup_failed', httpStatus: 503, description: 'Internal merchant-svc unreachable. Retryable.' },
    ],
    curlExample: `curl "https://api.trans-pay.app/api/v1/cashin/a5ecfddb-1234-5678-9012-abcdef012345" \\
  -H "x-transpay-client-id: <32-char client id>" \\
  -H "x-transpay-client-token: <48-char token>"

# or by your own reference:
curl "https://api.trans-pay.app/api/v1/cashin/MERCHANT-TRX-001" \\
  -H "x-transpay-client-id: <32-char client id>" \\
  -H "x-transpay-client-token: <48-char token>"`,
    responseExample: {
      data: {
        trxId: 'a5ecfddb-1234-…',
        referenceId: 'MERCHANT-TRX-001',
        trxStatus: 'paid',
        method: 'QRIS',
        amount: 10000,
        fee: 250,
        merchantRevenue: 9750,
        createdAt: '2026-05-29T03:51:30.027Z',
        paidAt: '2026-05-29T03:53:12.150Z',
        settleAt: '2026-05-29T10:00:00.000Z',
        settledAt: '2026-05-29T10:00:01.300Z',
        payersName: 'JOHN DOE',
        rrnNumber: '202605291234567',
        remark: 'Top up wallet',
      },
    },
    responseFields: [
      { path: 'data.trxId', type: 'string (uuid)', description: 'TransPay transaction id.' },
      { path: 'data.referenceId', type: 'string', description: 'Your own reference passed at create.' },
      { path: 'data.trxStatus', type: '"pending" | "paid" | "failed"', description: 'Current lifecycle state. `paid` means the customer paid; settlement may still be pending (see `settledAt`).' },
      { path: 'data.method', type: 'string', description: 'Payment method used — the `paymentMethodCode` from create (QRIS, a *VA virtual account, an e-wallet, ConvenienceStore, etc.).' },
      { path: 'data.amount', type: 'integer (IDR)', description: 'Amount the customer was billed.' },
      { path: 'data.fee', type: 'integer (IDR)', description: 'TransPay fee deducted from amount.' },
      { path: 'data.merchantRevenue', type: 'integer (IDR)', description: '`amount - fee` — credited to wallet on settlement.' },
      { path: 'data.createdAt', type: 'string (ISO8601 UTC)', description: 'When the trx was created.' },
      { path: 'data.paidAt', type: 'string | null', description: 'When the customer paid. null while pending/failed.' },
      { path: 'data.settleAt', type: 'string | null', description: 'Scheduled settlement time. By default: paid 00:00–11:59 GMT+7 → same day 19:00; paid 12:00–23:59 → next day 13:00. Payment methods under a settlement exception rule (e.g. BCAVA) settle H+1 at 13:00 and skip exception dates such as weekends.' },
      { path: 'data.settledAt', type: 'string | null', description: 'When wallet pending→active actually happened. null until settlement runs.' },
      { path: 'data.payersName', type: 'string | null', description: 'End customer name as reported by upstream. null when not provided.' },
      { path: 'data.rrnNumber', type: 'string | null', description: 'Reference number from the bank network — useful for cross-reconciliation.' },
    ],
    notes:
      'The QR code / payUrl from the original `/api/v1/cashin` response is NOT re-emitted here. Cache those client-side at create time — re-rendering an expired QR to your end customer is confusing. Status is the canonical state of the trx; webhooks deliver the same data push-style.',
  },
  {
    method: 'POST',
    path: '/api/v1/cashout/inquiry',
    title: 'Disbursement Inquiry (Step 1)',
    description:
      'Server-to-server disbursement (money-out) is a 2-step flow. Step 1 validates the beneficiary bank account with the provider and returns a single-use `trxId` (valid 30 min) that you pass to `POST /api/v1/cashout`. No money moves here. The merchant must be enabled for API disbursement by TransPay (OFF by default — money-out via API key is high-risk). EVERY request must additionally be HMAC-signed (see `x-transpay-signature` + notes). Rate-limited 10/minute per IP.',
    category: 'Server-to-Server (Merchant API)',
    scope: 'merchant-api',
    permissions: [],
    userType: 'external-only',
    baseUrl: 'https://api.trans-pay.app',
    requestHeaders: [
      { name: 'content-type', required: true, description: 'Must be application/json.', example: 'application/json' },
      { name: 'x-transpay-client-id', required: true, description: '32-char merchant client id.', example: 'Fm1p2iPUENI0sEFuBwpCj9Gz0yZAKjVF' },
      { name: 'x-transpay-client-token', required: true, description: '48-char merchant client token.', example: '<48-char token>' },
      { name: 'x-transpay-timestamp', required: true, description: 'Unix seconds. Rejected if more than 300s from server time (replay window).', example: '1750000000' },
      { name: 'x-transpay-signature', required: true, description: 'Hex HMAC-SHA256 of the signed message (see notes), keyed by your merchant webhook secret.', example: 'a3f19c…' },
    ],
    errorCodes: [
      { code: 'invalid_credentials', httpStatus: 401, description: 'API key headers missing/wrong length or unknown.' },
      { code: 'signature_required', httpStatus: 401, description: 'Timestamp or signature header missing.' },
      { code: 'signature_expired', httpStatus: 401, description: 'Timestamp outside the ±300s window.' },
      { code: 'signature_invalid', httpStatus: 401, description: 'HMAC does not match.' },
      { code: 'ip_not_whitelisted', httpStatus: 403, description: 'Source IP not on the merchant API IP whitelist (deny-all).' },
      { code: 'api_disbursement_disabled', httpStatus: 403, description: 'This merchant is not enabled for API disbursement.' },
      { code: 'merchant_suspended', httpStatus: 403, description: 'Merchant suspended — money-out is blocked.' },
      { code: 'amount_below_minimum', httpStatus: 400, description: 'Below the merchant cashout minimum.' },
      { code: 'amount_exceeds_limit', httpStatus: 400, description: 'Above the merchant cashout maximum.' },
      { code: 'bank_not_supported', httpStatus: 400, description: '`bankCode` not in the supported bank list (or disabled).' },
      { code: 'duplicate_merchant_ref', httpStatus: 409, description: '`referenceId` was already used by your merchant (any status, cash-in or cash-out). Use a new value.' },
      { code: 'reference_id_unavailable', httpStatus: 409, description: '`referenceId` is already in use as a provider order id platform-wide. Use a more specific value (e.g. prefix it with your own merchant code).' },
      { code: 'inquiry_rejected', httpStatus: 400, description: 'Provider could not validate the beneficiary account.' },
    ],
    curlExample: `# signed message = timestamp + "\\n" + clientId + "\\n" + countryCode + "\\n" + bankCode + "\\n" + accountNumber + "\\n" + amount
#                  + ( "\\n" + referenceId   only when the body contains referenceId )
# x-transpay-signature = hex( HMAC_SHA256(merchantWebhookSecret, signedMessage) )
curl -X POST "https://api.trans-pay.app/api/v1/cashout/inquiry" \\
  -H "content-type: application/json" \\
  -H "x-transpay-client-id: <32-char client id>" \\
  -H "x-transpay-client-token: <48-char token>" \\
  -H "x-transpay-timestamp: 1750000000" \\
  -H "x-transpay-signature: <hex hmac>" \\
  -d '{ "countryCode": "IDN", "bankCode": "CENAIDJA", "accountNumber": "1234567890", "holderName": "JOHN DOE", "amount": 100000, "referenceId": "WD-0001" }'`,
    requestBodyExample: {
      countryCode: 'IDN',
      bankCode: 'CENAIDJA',
      accountNumber: '1234567890',
      holderName: 'JOHN DOE',
      amount: 100000,
      referenceId: 'WD-0001',
    },
    responseFields: [
      { path: 'trxId', type: 'string (uuid)', description: 'Single-use id (30 min) — pass to POST /api/v1/cashout to submit.' },
      { path: 'referenceId', type: 'string', description: 'Your `referenceId` (auto-generated `WD-…` when omitted). Bound to this `trxId` here and forwarded to the payment provider as the order id for BOTH the inquiry and the payout, so sending it on this step is preferred. Sending it only on step 2 still works (the provider inquiry is re-run under your value); sending two different values is rejected. Must be unique — 1 … 64 characters.' },
      { path: 'verifiedHolderName', type: 'string', description: 'Account holder name confirmed by the bank — confirm before submitting.' },
      { path: 'bankCode', type: 'string', description: 'Echo of the bank code.' },
      { path: 'amount', type: 'integer (IDR)', description: 'Echo of the amount.' },
      { path: 'fee', type: 'integer (IDR)', description: 'TransPay disbursement fee.' },
      { path: 'totalDebit', type: 'integer (IDR)', description: '`amount + fee` — debited from your wallet when you submit step 2.' },
    ],
    notes:
      'SIGNED MESSAGE (inquiry) = the 6 values [timestamp, clientId, countryCode, bankCode, accountNumber, amount] joined by newline ("\\n"); when the body contains `referenceId`, append it as a 7th value. HMAC-SHA256 with your merchant webhook secret, hex-encode → x-transpay-signature. (The same secret TransPay uses to sign webhooks it sends you.) `referenceId` is best sent at THIS step because it is the provider order id for both remit calls — then pass the same value (or omit it) on POST /api/v1/cashout. Sending it only on POST /api/v1/cashout is also accepted (TransPay re-runs the provider inquiry under your value); sending two different values is rejected with reference_id_mismatch.',
  },
  {
    method: 'POST',
    path: '/api/v1/cashout',
    title: 'Create Disbursement (Step 2)',
    description:
      'Step 2: submit the payout for a `trxId` returned by `POST /api/v1/cashout/inquiry`. Bank/account/amount come from the server-side inquiry context — you CANNOT change them here (prevents tamper-between-steps). Reserves `amount + fee` from your wallet (atomic; fails with `insufficient_balance` if active balance is too low), then submits to the provider. The final paid/failed result arrives via your disbursement webhook and `GET /api/v1/cashout/:idOrRef`. One submit per `trxId` (replay-safe). Same API-key + HMAC + timestamp headers as step 1. Rate-limited 5/minute per IP.',
    category: 'Server-to-Server (Merchant API)',
    scope: 'merchant-api',
    permissions: [],
    userType: 'external-only',
    baseUrl: 'https://api.trans-pay.app',
    requestHeaders: [
      { name: 'content-type', required: true, description: 'Must be application/json.', example: 'application/json' },
      { name: 'x-transpay-client-id', required: true, description: '32-char merchant client id.', example: 'Fm1p2iPUENI0sEFuBwpCj9Gz0yZAKjVF' },
      { name: 'x-transpay-client-token', required: true, description: '48-char merchant client token.', example: '<48-char token>' },
      { name: 'x-transpay-timestamp', required: true, description: 'Unix seconds (±300s window).', example: '1750000000' },
      { name: 'x-transpay-signature', required: true, description: 'Hex HMAC-SHA256 of the signed message (see notes).', example: 'b7c4e1…' },
    ],
    errorCodes: [
      { code: 'signature_invalid', httpStatus: 401, description: 'HMAC missing/expired/mismatch.' },
      { code: 'api_disbursement_disabled', httpStatus: 403, description: 'Merchant not enabled for API disbursement.' },
      { code: 'merchant_suspended', httpStatus: 403, description: 'Merchant suspended.' },
      { code: 'inquiry_missing_or_expired', httpStatus: 400, description: '`trxId` has no inquiry context (expired or unknown).' },
      { code: 'inquiry_already_used', httpStatus: 409, description: 'This `trxId` was already submitted (replay).' },
      { code: 'insufficient_balance', httpStatus: 400, description: 'Active wallet balance < amount + fee.' },
      { code: 'reference_id_mismatch', httpStatus: 400, description: 'You sent a `referenceId` at `/inquiry` AND a different one here. Send the same value on both steps, or send it on one step only. (If `/inquiry` had no `referenceId`, the one you send here is adopted: TransPay re-runs the provider account inquiry under it before submitting the payout.)' },
      { code: 'duplicate_merchant_ref', httpStatus: 409, description: '`referenceId` was already used by your merchant (any status, cash-in or cash-out).' },
      { code: 'reference_id_unavailable', httpStatus: 409, description: '`referenceId` is already in use as a provider order id platform-wide.' },
      { code: 'provider_rejected', httpStatus: 400, description: 'Provider rejected the payout; your wallet reserve was refunded automatically.' },
    ],
    curlExample: `# signed message = timestamp + "\\n" + clientId + "\\n" + trxId + "\\n" + referenceId(or empty)
# referenceId is OPTIONAL here. If /inquiry already had one, this must be the same value (or omitted);
# if /inquiry had none, the value you send here becomes the provider order id.
curl -X POST "https://api.trans-pay.app/api/v1/cashout" \\
  -H "content-type: application/json" \\
  -H "x-transpay-client-id: <32-char client id>" \\
  -H "x-transpay-client-token: <48-char token>" \\
  -H "x-transpay-timestamp: 1750000000" \\
  -H "x-transpay-signature: <hex hmac>" \\
  -d '{ "trxId": "<trxId from /inquiry>", "referenceId": "WD-0001", "remark": "Vendor payout" }'`,
    requestBodyExample: {
      trxId: '<trxId from /inquiry>',
      referenceId: 'WD-0001',
      remark: 'Vendor payout',
    },
    responseFields: [
      { path: 'data.trxId', type: 'string (uuid)', description: 'TransPay disbursement id.' },
      { path: 'data.referenceId', type: 'string', description: 'The `referenceId` bound at `/inquiry` (yours, or the auto-generated `WD-…`). This exact value is the order id at the payment provider.' },
      { path: 'data.trxStatus', type: '"pending"', description: 'Always pending on submit; the webhook delivers paid/failed.' },
      { path: 'data.amount', type: 'integer (IDR)', description: 'Amount transferred.' },
      { path: 'data.fee', type: 'integer (IDR)', description: 'Disbursement fee.' },
      { path: 'data.merchantRevenue', type: 'integer (IDR)', description: 'Wallet impact `-(amount + fee)`.' },
      { path: 'data.beneficiary', type: 'object', description: '{ bankCode, bankName, accountNumber, holderName } — from the inquiry.' },
    ],
    notes:
      'SIGNED MESSAGE (create) = [timestamp, clientId, trxId, referenceId(or empty string)] joined by newline. There is NO password/MFA (server-to-server) — security rests on the secret API key + HMAC signature + IP whitelist + the per-merchant opt-in. An internal operator enables a merchant for this on the merchant page (`merchants.api_disbursement.manage`).',
  },
  {
    method: 'GET',
    path: '/api/v1/cashout/:idOrRef',
    title: 'Check Disbursement Status',
    description:
      'Server-to-server status check for a disbursement transaction. Accepts EITHER the TransPay `trxId` (UUID v4) OR the disbursement `referenceId` (e.g. `WD-XXXXX`) as the path param. Use this as a webhook fallback or to reconcile in-flight disbursements. Rate-limited 60 requests/minute per IP.',
    category: 'Server-to-Server (Merchant API)',
    scope: 'merchant-api',
    permissions: [],
    userType: 'external-only',
    baseUrl: 'https://api.trans-pay.app',
    requestHeaders: [
      { name: 'x-transpay-client-id', required: true, description: '32-char merchant client id.', example: 'Fm1p2iPUENI0sEFuBwpCj9Gz0yZAKjVF' },
      { name: 'x-transpay-client-token', required: true, description: '48-char merchant client token.', example: '<48-char token>' },
    ],
    errorCodes: [
      { code: 'invalid_credentials', httpStatus: 401, description: 'Headers missing or wrong length.' },
      { code: 'merchant_suspended', httpStatus: 403, description: 'Merchant has been suspended.' },
      { code: 'trx_not_found', httpStatus: 404, description: 'No disbursement found for this `idOrRef` under your merchant.' },
      { code: 'merchant_lookup_failed', httpStatus: 503, description: 'Internal merchant-svc unreachable. Retryable.' },
    ],
    curlExample: `curl "https://api.trans-pay.app/api/v1/cashout/95d1dc9c-908d-4454-82a0-f2e972487153" \\
  -H "x-transpay-client-id: <32-char client id>" \\
  -H "x-transpay-client-token: <48-char token>"

# or by reference:
curl "https://api.trans-pay.app/api/v1/cashout/WD-MPQVMO2R" \\
  -H "x-transpay-client-id: <32-char client id>" \\
  -H "x-transpay-client-token: <48-char token>"`,
    responseExample: {
      data: {
        trxId: '95d1dc9c-908d-4454-…',
        referenceId: 'WD-MPQVMO2R',
        trxStatus: 'paid',
        method: 'BANK_TRANSFER',
        amount: 100000,
        fee: 6500,
        merchantRevenue: -106500,
        remark: 'Vendor payout #2026-001',
        createdAt: '2026-05-29T05:00:00.000Z',
        paidAt: '2026-05-29T05:00:08.532Z',
        beneficiary: {
          bankCode: 'CENAIDJA',
          bankName: 'Bank BCA',
          accountNumber: '1234567890',
          holderName: 'JOHN DOE',
        },
      },
    },
    responseFields: [
      { path: 'data.trxId', type: 'string (uuid)', description: 'TransPay disbursement id.' },
      { path: 'data.referenceId', type: 'string', description: 'Server-generated short reference (`WD-XXXX`).' },
      { path: 'data.trxStatus', type: '"pending" | "paid" | "failed"', description: '`paid` = funds reached the beneficiary bank. `failed` = the disbursement was rejected and your wallet was refunded.' },
      { path: 'data.amount', type: 'integer (IDR)', description: 'Amount transferred.' },
      { path: 'data.fee', type: 'integer (IDR)', description: 'TransPay disbursement fee.' },
      { path: 'data.merchantRevenue', type: 'integer (IDR)', description: 'Wallet impact (negative for cashout — `-(amount + fee)`).' },
      { path: 'data.createdAt', type: 'string (ISO8601 UTC)', description: 'When the disbursement was submitted.' },
      { path: 'data.paidAt', type: 'string | null', description: 'When the upstream callback marked it paid. null while pending.' },
      { path: 'data.beneficiary.bankCode', type: 'string', description: 'SWIFT-style code passed at inquiry.' },
      { path: 'data.beneficiary.bankName', type: 'string', description: 'Human-readable bank name.' },
      { path: 'data.beneficiary.accountNumber', type: 'string', description: 'Beneficiary account number.' },
      { path: 'data.beneficiary.holderName', type: 'string', description: 'Holder name as confirmed by the inquiry.' },
    ],
    notes:
      'Same auth model as `/api/v1/cashin`. Despite the dashboard wizard using a session, this status endpoint is server-to-server so your backend can poll without holding a user session.',
  },
  {
    method: 'GET',
    path: '/api/v1/wallet',
    title: 'Get Wallet Balance',
    description:
      'Server-to-server read of your own wallet balances — the same figures the dashboard shows. There is NO path parameter: the endpoint always returns the wallet belonging to the credentials you authenticate with, so it can never expose another merchant. Rate-limited 20 requests/minute per IP.',
    category: 'Server-to-Server (Merchant API)',
    scope: 'merchant-api',
    permissions: [],
    userType: 'external-only',
    baseUrl: 'https://api.trans-pay.app',
    requestHeaders: [
      { name: 'x-transpay-client-id', required: true, description: '32-char merchant client id.', example: 'Fm1p2iPUENI0sEFuBwpCj9Gz0yZAKjVF' },
      { name: 'x-transpay-client-token', required: true, description: '48-char merchant client token.', example: '<48-char token>' },
    ],
    errorCodes: [
      { code: 'invalid_credentials', httpStatus: 401, description: 'Headers missing, wrong length, or the credential pair does not match. Identical response for an unknown client id and a bad token — we never reveal which half was wrong.' },
      { code: 'ip_whitelist_required', httpStatus: 403, description: 'Your merchant has no enabled, unexpired API IP whitelist entries. The whitelist is deny-all: with an empty list every API call is rejected.' },
      { code: 'ip_not_whitelisted', httpStatus: 403, description: 'The request source IP is not on your API IP whitelist.' },
      { code: 'api_disabled', httpStatus: 403, description: 'API access is switched off for your merchant.' },
      { code: 'merchant_suspended', httpStatus: 403, description: 'Merchant has been suspended.' },
      { code: 'wallet_not_found', httpStatus: 404, description: 'No wallet row for this merchant — an internal inconsistency. Contact support.' },
      { code: 'rate_limited', httpStatus: 429, description: 'More than 20 requests in one minute from this IP.' },
    ],
    curlExample: `curl "https://api.trans-pay.app/api/v1/wallet" \\
  -H "x-transpay-client-id: <32-char client id>" \\
  -H "x-transpay-client-token: <48-char token>"`,
    responseExample: {
      data: {
        merchantId: '02094a75-6b66-4a77-94f4-68842f9bc011',
        pendingBalance: 250000,
        activeBalance: 1500000,
        reservedBalance: 0,
        lifetimeDeposit: 4750000,
        lifetimeDisbursement: 3000000,
        lifetimeFee: 33250,
        updatedAt: '2026-08-04T17:01:01.746Z',
      },
    },
    responseFields: [
      { path: 'data.merchantId', type: 'string (uuid)', description: 'Your merchant id — always the owner of the credentials used.' },
      { path: 'data.pendingBalance', type: 'integer (IDR)', description: 'Paid deposits not yet settled. Moves to `activeBalance` at settlement time (see `settleAt` on the deposit).' },
      { path: 'data.activeBalance', type: 'integer (IDR)', description: 'Settled funds available to disburse.' },
      { path: 'data.reservedBalance', type: 'integer (IDR)', description: 'Locked against disbursements currently in flight. Released on success or failure.' },
      { path: 'data.lifetimeDeposit', type: 'integer (IDR)', description: 'Cumulative settled deposits since account opening.' },
      { path: 'data.lifetimeDisbursement', type: 'integer (IDR)', description: 'Cumulative successful disbursements.' },
      { path: 'data.lifetimeFee', type: 'integer (IDR)', description: 'Cumulative TransPay fees charged.' },
      { path: 'data.updatedAt', type: 'string (ISO8601 UTC)', description: 'When the wallet last changed. Use this to detect movement between polls.' },
    ],
    notes:
      'All amounts are whole IDR integers and are never negative. This is a point-in-time read — for balance CHANGES prefer the settlement webhook over polling; the 20/min limit is sized for reconciliation, not for a live balance ticker. `updatedAt` is the cheapest way to tell whether anything moved since your last call.',
  },

  // ====== Merchants ======
  {
    method: 'GET',
    path: '/api/merchants',
    title: 'List Merchants',
    description: 'Cursor-paginated list of all merchants. Internal users only.',
    category: 'Merchants',
    scope: 'dashboard',
    permissions: ['merchants.list.view'],
    userType: 'internal-only',
  },
  {
    method: 'GET',
    path: '/api/merchants/:id',
    title: 'Get Merchant',
    description: 'Returns merchant detail. External users may only fetch their own merchant (server force-applies the scope check).',
    category: 'Merchants',
    scope: 'dashboard',
    permissions: ['merchants.list.view'],
    userType: 'any',
  },
  {
    method: 'POST',
    path: '/api/merchants',
    title: 'Create Merchant',
    description: 'Create a merchant. Auto-issues a MerchantAdmin invite for the bootstrap email; the invite token is returned ONCE in the response.',
    category: 'Merchants',
    scope: 'dashboard',
    permissions: ['merchants.create'],
    userType: 'internal-only',
  },
  {
    method: 'PATCH',
    path: '/api/merchants/:id',
    title: 'Update Merchant',
    description: 'Partial update — fee config, upstream provider config, webhook URLs, API access flag.',
    category: 'Merchants',
    scope: 'dashboard',
    permissions: ['merchants.edit'],
    userType: 'internal-only',
  },
  {
    method: 'POST',
    path: '/api/merchants/:id/suspend',
    title: 'Suspend Merchant',
    description: 'Mark the merchant suspended. API calls and dashboard logins are blocked until unsuspended.',
    category: 'Merchants',
    scope: 'dashboard',
    permissions: ['merchants.suspend'],
    userType: 'internal-only',
  },
  {
    method: 'POST',
    path: '/api/merchants/:id/unsuspend',
    title: 'Unsuspend Merchant',
    description: 'Lift the suspension.',
    category: 'Merchants',
    scope: 'dashboard',
    permissions: ['merchants.suspend'],
    userType: 'internal-only',
  },
  {
    method: 'POST',
    path: '/api/merchants/:id/regenerate-credentials',
    title: 'Rotate Merchant API Credentials',
    description: 'Rotate the merchant\'s API client_id + token. The new token is shown ONCE in the response.',
    category: 'Merchants',
    scope: 'dashboard',
    permissions: ['merchants.credentials.manage'],
    userType: 'internal-only',
  },
  {
    method: 'POST',
    path: '/api/merchants/:id/regenerate-webhook-secret',
    title: 'Rotate Webhook Secret',
    description: 'Rotate the HMAC secret used to sign outbound merchant webhooks. New secret is shown ONCE.',
    category: 'Merchants',
    scope: 'dashboard',
    permissions: ['merchants.webhook.manage'],
    userType: 'internal-only',
  },

  // ====== Deposits ======
  {
    method: 'GET',
    path: '/api/deposits/page',
    title: 'List Deposits',
    description: 'Filter-rich list of deposits. Pass `page` (1-based) for numbered pagination — the response then carries `total` and `pageCount`; without it the cursor protocol (`cursor` / `nextCursor`) applies. External users see only their own merchant\'s rows.',
    category: 'Deposits',
    scope: 'dashboard',
    permissions: ['deposits.list.view'],
    userType: 'any',
  },
  {
    method: 'GET',
    path: '/api/deposits/summary',
    title: 'Deposit List Summary',
    description: 'Aggregates over the SAME filter set as /api/deposits/page (pagination ignored): totals (count / amount / fee / merchant revenue) for all matching rows, a per-status breakdown and a per-payment-method breakdown. Powers the overview strip above the list filters.',
    category: 'Deposits',
    scope: 'dashboard',
    permissions: ['deposits.list.view'],
    userType: 'any',
  },
  {
    method: 'GET',
    path: '/api/deposits/:trxId/detail',
    title: 'Get Deposit Detail',
    description: 'Returns the full deposit row. Internal staff with the upstream-provider audit permission also see upstream-provider identifiers.',
    category: 'Deposits',
    scope: 'dashboard',
    permissions: ['deposits.detail.view'],
    userType: 'any',
  },
  {
    method: 'GET',
    path: '/api/deposits/:trxId/api-log',
    title: 'Deposit API Activity Log',
    description: 'Per-trx HTTP audit timeline — your cashin call in and webhook delivery attempts out. Internal staff with the upstream-provider audit permission additionally see upstream-provider exchanges.',
    category: 'Deposits',
    scope: 'dashboard',
    permissions: ['audit.api_log.view'],
    userType: 'any',
  },
  {
    method: 'POST',
    path: '/api/deposits/:trxId/resend-webhook',
    title: 'Resend Deposit Webhook',
    description: 'Re-queues the last deposit webhook (paid / failed) to the merchant\'s CURRENT webhook URL with a new delivery id.',
    category: 'Deposits',
    scope: 'dashboard',
    permissions: ['webhook.delivery.retry', 'webhook.delivery.internal.retry'],
    userType: 'any',
  },
  {
    method: 'POST',
    path: '/api/deposits/:trxId/status',
    title: 'Update Deposit Status (manual)',
    description: 'Internal-only manual override: force a deposit to pending / paid / failed when the provider callback never arrived or was wrong. The wallet effect mirrors the provider callback (paid → pending balance credit + settlement schedule; paid → failed → reversal). Body: { status, resendCallback }. With resendCallback=true a fresh deposit.paid / deposit.failed webhook is queued to the merchant. paid → pending is rejected (set failed first). Audited.',
    category: 'Deposits',
    scope: 'dashboard',
    permissions: ['deposits.status.update'],
    userType: 'internal-only',
    requestBodyExample: { status: 'paid', resendCallback: true },
    errorCodes: [
      { code: 'forbidden', httpStatus: 403, description: 'Caller is not an internal user.' },
      { code: 'permission_denied', httpStatus: 403, description: 'Caller lacks `deposits.status.update`.' },
      { code: 'status_unchanged', httpStatus: 400, description: 'Deposit already has the requested status.' },
      { code: 'invalid_transition', httpStatus: 400, description: 'paid → pending is not allowed; go through failed.' },
      { code: 'status_changed_concurrently', httpStatus: 409, description: 'A provider callback changed the status while updating. Reload and retry.' },
      { code: 'wallet_invariant_violation', httpStatus: 409, description: 'The wallet movement would drive a balance negative.' },
    ],
  },

  // ====== Disbursements (cash-out, dashboard) ======
  // The /api/disbursements endpoints are SESSION-auth (browser cookie),
  // not merchant-API. Merchant Admin / Merchant Finance roles call them
  // from the dashboard wizard. There is no merchant-API S2S equivalent
  // — cash-out always goes through the dashboard so it picks up password
  // step-up + MFA per spec.
  {
    method: 'GET',
    path: '/api/disbursements/page',
    title: 'List Disbursements',
    description:
      'Cursor-paginated list of disbursements with rich filtering (trxId, reff, merchant, status, bank, holderName ILIKE, date ranges, amount/fee ranges). External users see only their own merchant\'s rows.',
    category: 'Disbursements',
    scope: 'dashboard',
    permissions: ['disbursements.list.view'],
    userType: 'any',
    requestHeaders: [
      { name: 'cookie', required: true, description: 'Session cookie (transpay_session) set by /api/auth/login.' },
    ],
    responseFields: [
      { path: 'rows[].trxId', type: 'string (uuid)', description: 'Disbursement id.' },
      { path: 'rows[].referenceId', type: 'string', description: 'Auto-generated or operator-supplied reference (WD-...).' },
      { path: 'rows[].amount', type: 'integer (IDR)', description: 'Transfer amount.' },
      { path: 'rows[].fee', type: 'integer (IDR)', description: 'TransPay disbursement fee.' },
      { path: 'rows[].merchantRevenue', type: 'integer (IDR)', description: 'Signed wallet impact. For cash-out this displays as a negative number in the UI.' },
      { path: 'rows[].trxStatus', type: '"pending" | "paid" | "failed"', description: 'Lifecycle: pending → paid (callback success) or failed (refund issued).' },
      { path: 'rows[].bankName', type: 'string | null', description: 'Beneficiary bank name.' },
      { path: 'rows[].accountNumber', type: 'string | null', description: 'Beneficiary account number.' },
      { path: 'rows[].holderName', type: 'string | null', description: 'Account holder name as confirmed by the bank inquiry.' },
      { path: 'nextCursor', type: 'string | null', description: 'Opaque cursor for the next page. null = end of list.' },
      { path: 'hasMore', type: 'boolean', description: 'Convenience flag; true iff nextCursor is non-null.' },
    ],
  },
  {
    method: 'GET',
    path: '/api/disbursements/:trxId/detail',
    title: 'Get Disbursement Detail',
    description: 'Single-row detail. External users see their merchant\'s rows only. Internal staff with the upstream-provider audit permission also see upstream-provider identifiers and request/response bodies.',
    category: 'Disbursements',
    scope: 'dashboard',
    permissions: ['disbursements.detail.view'],
    userType: 'any',
  },
  {
    method: 'GET',
    path: '/api/disbursements/:trxId/api-log',
    title: 'Disbursement API Activity Log',
    description: 'Per-trx HTTP audit timeline — merchant inbound call(s) and webhook delivery attempts out. Internal staff with the upstream-provider audit permission additionally see upstream-provider exchanges.',
    category: 'Disbursements',
    scope: 'dashboard',
    permissions: ['audit.api_log.view'],
    userType: 'any',
  },
  {
    method: 'POST',
    path: '/api/disbursements/inquiry',
    title: 'Disbursement Step 1 — Account Inquiry',
    description:
      'Pre-flight validation of the beneficiary bank account before the user commits. Returns the bank-confirmed holder name + computed fee + total debit so the user reviews everything before submitting. Issues a `trxId` + short-lived `inquiryToken` cached in Redis (30 min) that the create call must reference. Server-side rate-limit: 10/min per IP to defend against account-walking probes.',
    category: 'Disbursements',
    scope: 'dashboard',
    permissions: ['disbursements.create'],
    userType: 'external-only',
    requestHeaders: [
      { name: 'content-type', required: true, description: 'application/json' },
      { name: 'cookie', required: true, description: 'Session cookie from /api/auth/login.' },
    ],
    errorCodes: [
      { code: 'missing_session', httpStatus: 401, description: 'No session cookie / token.' },
      { code: 'permission_denied', httpStatus: 403, description: 'Caller lacks `disbursements.create`.' },
      { code: 'invalid_body', httpStatus: 400, description: 'Validation error — see detail.fieldErrors.' },
      { code: 'amount_below_minimum', httpStatus: 400, description: 'Amount < merchant\'s configured cashout min.' },
      { code: 'amount_exceeds_limit', httpStatus: 400, description: 'Amount > merchant\'s configured cashout max.' },
      { code: 'insufficient_balance', httpStatus: 400, description: 'Wallet active balance < amount + fee.' },
      { code: 'bank_not_supported', httpStatus: 400, description: 'bankCode not in the platform bank list.' },
      { code: 'inquiry_rejected', httpStatus: 400, description: 'Bank returned account-not-found / holder-mismatch / similar.' },
      { code: 'provider_not_configured', httpStatus: 503, description: 'Upstream provider not configured for this merchant.' },
      { code: 'upstream_failed', httpStatus: 502, description: 'Upstream provider error.' },
    ],
    requestBodyExample: {
      countryCode: 'IDN',
      bankCode: 'CENAIDJA',
      accountNumber: '1234567890',
      holderName: 'JOHN DOE',
      amount: 100000,
    },
    responseExample: {
      trxId: 'a5ecfddb-…',
      merchantOrderId: '90d4f8c2…',
      inquiryToken: '2026060412345678…',
      verifiedHolderName: 'JOHN DOE',
      bankCode: 'CENAIDJA',
      bankName: 'Bank BCA',
      accountNumber: '1234567890',
      amount: 100000,
      fee: 6500,
      totalDebit: 106500,
    },
    notes:
      'Wallet flow: inquiry does NOT move money. It only confirms the account is valid and returns the trxId you reuse in Step 2. Internal users cannot call this — disbursement is merchant-side.',
  },
  {
    method: 'POST',
    path: '/api/disbursements',
    title: 'Disbursement Step 2 — Create (commit)',
    description:
      'Commits the disbursement. Verifies password step-up + MFA TOTP (or backup code), reserves wallet (active → reserved), then calls the upstream provider. On upstream accept the row stays pending until the upstream callback flips it to paid (reserved → 0, settled). On upstream reject the reservation is refunded (reserved → active) and the row is marked failed. Server-side rate-limit: 5/min per IP.',
    category: 'Disbursements',
    scope: 'dashboard',
    permissions: ['disbursements.create'],
    userType: 'external-only',
    requestHeaders: [
      { name: 'content-type', required: true, description: 'application/json' },
      { name: 'cookie', required: true, description: 'Session cookie from /api/auth/login.' },
    ],
    requestBodyExample: {
      trxId: 'a5ecfddb-…',
      password: '••••••••',
      mfaCode: '123456',
      remark: 'Withdrawal #42',
    },
    responseExample: {
      data: {
        trxId: 'a5ecfddb-…',
        referenceId: 'WD-MPQVMO2R',
        trxStatus: 'pending',
        amount: 100000,
        fee: 6500,
        merchantRevenue: -106500,
        beneficiary: {
          bankCode: 'CENAIDJA',
          bankName: 'Bank BCA',
          accountNumber: '1234567890',
          holderName: 'JOHN DOE',
        },
      },
    },
    errorCodes: [
      { code: 'missing_session', httpStatus: 401, description: 'No session cookie.' },
      { code: 'permission_denied', httpStatus: 403, description: 'Caller lacks `disbursements.create`.' },
      { code: 'invalid_body', httpStatus: 400, description: 'Validation error.' },
      { code: 'inquiry_missing_or_expired', httpStatus: 400, description: 'trxId has no cached inquiry context (>30 min) or never inquired.' },
      { code: 'inquiry_already_used', httpStatus: 409, description: 'trxId already submitted or in-flight (one-shot enforcement).' },
      { code: 'invalid_password', httpStatus: 401, description: 'Step-up password incorrect.' },
      { code: 'mfa_required', httpStatus: 403, description: 'User has no active MFA. Enroll via /api/auth/mfa/enroll-init first.' },
      { code: 'invalid_mfa_code', httpStatus: 401, description: 'TOTP code did not verify and was not an unused backup code.' },
      { code: 'amount_below_minimum', httpStatus: 400, description: 'Amount < merchant cashout min (could have changed since inquiry).' },
      { code: 'amount_exceeds_limit', httpStatus: 400, description: 'Amount > merchant cashout max.' },
      { code: 'bank_not_supported', httpStatus: 400, description: 'Bank disabled between inquiry and create.' },
      { code: 'forbidden', httpStatus: 403, description: 'Inquiry belongs to another merchant or another user.' },
      { code: 'provider_rejected', httpStatus: 400, description: 'Upstream provider rejected the order (e.g. bank closed / bad signature).' },
      { code: 'upstream_failed', httpStatus: 502, description: 'Upstream provider error (timeout / 5xx / signature invalid).' },
    ],
    notes:
      'Requires MFA. Each call consumes one TOTP code or one unused backup code (one-shot). The trxId can only be submitted once — retry-with-same-trxId returns 409 `inquiry_already_used`. To retry a failed disbursement, run a fresh inquiry to get a new trxId.',
  },
  {
    method: 'GET',
    path: '/api/disbursements/summary',
    title: 'Disbursement List Summary',
    description: 'Aggregates over the SAME filter set as /api/disbursements/page (pagination ignored): totals for all matching rows, a per-status breakdown and a per-destination-bank breakdown. merchantRevenue is the signed wallet impact (negative).',
    category: 'Disbursements',
    scope: 'dashboard',
    permissions: ['disbursements.list.view'],
    userType: 'any',
  },
  {
    method: 'POST',
    path: '/api/disbursements/:trxId/resend-webhook',
    title: 'Resend Webhook',
    description: 'Re-queues the merchant webhook delivery for this disbursement. Useful when the merchant\'s receiving endpoint was down at the original send.',
    category: 'Disbursements',
    scope: 'dashboard',
    permissions: ['webhook.delivery.retry', 'webhook.delivery.internal.retry'],
    userType: 'any',
  },
  {
    method: 'POST',
    path: '/api/disbursements/:trxId/status',
    title: 'Update Disbursement Status (manual)',
    description: 'Internal-only manual override: force a disbursement to pending / paid / failed. The wallet effect mirrors the provider callback (pending → paid clears the reserve; pending → failed refunds it to active; paid → failed returns the funds; failed → paid re-debits active). Body: { status, resendCallback }. With resendCallback=true a fresh disbursement.paid / disbursement.failed webhook is queued to the merchant. paid → pending is rejected (set failed first). Audited.',
    category: 'Disbursements',
    scope: 'dashboard',
    permissions: ['disbursements.status.update'],
    userType: 'internal-only',
    requestBodyExample: { status: 'failed', resendCallback: true },
    errorCodes: [
      { code: 'forbidden', httpStatus: 403, description: 'Caller is not an internal user.' },
      { code: 'permission_denied', httpStatus: 403, description: 'Caller lacks `disbursements.status.update`.' },
      { code: 'status_unchanged', httpStatus: 400, description: 'Disbursement already has the requested status.' },
      { code: 'invalid_transition', httpStatus: 400, description: 'paid → pending is not allowed; go through failed.' },
      { code: 'insufficient_balance', httpStatus: 400, description: 'failed → paid/pending needs amount + fee available in the merchant\'s active balance.' },
      { code: 'status_changed_concurrently', httpStatus: 409, description: 'A provider callback changed the status while updating. Reload and retry.' },
    ],
  },
  {
    method: 'GET',
    path: '/api/banks',
    title: 'List Supported Banks',
    description: 'Returns the operator-enabled bank list (Indonesia banks + e-wallets supported by the upstream remit provider). Used to populate the bank dropdown in the disbursement wizard.',
    category: 'Disbursements',
    scope: 'dashboard',
    permissions: ['disbursements.create'],
    userType: 'any',
  },

  // ====== MFA (per-user self-service) ======
  // All four endpoints operate on the caller's OWN user row only; no
  // additional permission needed beyond an authenticated session.
  {
    method: 'GET',
    path: '/api/auth/mfa/status',
    title: 'My MFA Status',
    description: 'Returns whether MFA is enabled, when it was enrolled, when it was last used, and how many backup codes are unused.',
    category: 'MFA',
    scope: 'dashboard',
    permissions: [],
    userType: 'any',
    responseExample: {
      enabled: true,
      enrolledAt: '2026-06-04T05:00:00.000Z',
      lastUsedAt: '2026-06-04T07:15:23.000Z',
      backupCodesRemaining: 10,
    },
  },
  {
    method: 'POST',
    path: '/api/auth/mfa/enroll-init',
    title: 'Begin MFA Enrollment',
    description:
      'Generate a fresh TOTP secret, KMS-wrap it as PENDING (not yet active), and return it + an otpauth:// URI + a base64 PNG QR code that an authenticator app (Google Authenticator / Authy / 1Password / Bitwarden) can scan. Idempotent: calling again overwrites the pending secret. Rate-limited 10/min per IP.',
    category: 'MFA',
    scope: 'dashboard',
    permissions: [],
    userType: 'any',
    responseExample: {
      secret: 'CAS3MRO5MR7UUDLYL2IFPF2L5UXWJ2V4',
      otpauthUri: 'otpauth://totp/TransPay:user%40transpay.com?secret=...&issuer=TransPay',
      qrDataUrl: 'data:image/png;base64,iVBORw0K...',
      issuer: 'TransPay',
      label: 'user@transpay.com',
    },
    errorCodes: [
      { code: 'mfa_already_enabled', httpStatus: 409, description: 'MFA is currently active. Disable it first if you want to re-enroll.' },
      { code: 'kms_wrap_failed', httpStatus: 503, description: 'KMS unavailable — retryable.' },
    ],
  },
  {
    method: 'POST',
    path: '/api/auth/mfa/enroll-verify',
    title: 'Complete MFA Enrollment',
    description:
      'Submit the first 6-digit code from the authenticator. On success, promotes the pending secret to active, mints 10 backup codes, and returns them ONCE — they are argon2-hashed before persistence so a DB-only breach cannot recover them. Rate-limited 5/min per IP.',
    category: 'MFA',
    scope: 'dashboard',
    permissions: [],
    userType: 'any',
    requestBodyExample: { code: '123456' },
    responseExample: {
      enabled: true,
      backupCodes: ['5L3O-EESC', '7NIF-HNDC', 'FAJL-ZJYA', '...'],
      backupCodesNote: 'Save these 10 backup codes …',
    },
    errorCodes: [
      { code: 'no_pending_enroll', httpStatus: 400, description: 'You must call /enroll-init before /enroll-verify.' },
      { code: 'invalid_code', httpStatus: 400, description: 'Code did not verify against the pending secret. The secret might be mis-typed in the authenticator, or the device clock is drifting.' },
      { code: 'mfa_already_enabled', httpStatus: 409, description: 'MFA already active.' },
    ],
  },
  {
    method: 'POST',
    path: '/api/auth/mfa/disable',
    title: 'Disable My MFA',
    description: 'Disable MFA. Requires BOTH account password AND a current valid TOTP code (proof of identity + proof of device). Backup codes are wiped on disable.',
    category: 'MFA',
    scope: 'dashboard',
    permissions: [],
    userType: 'any',
    requestBodyExample: { password: '••••••••', code: '123456' },
    responseExample: { enabled: false },
    errorCodes: [
      { code: 'mfa_not_enabled', httpStatus: 400, description: 'MFA is not currently active.' },
      { code: 'invalid_password', httpStatus: 401, description: 'Password incorrect.' },
      { code: 'invalid_code', httpStatus: 400, description: 'MFA code did not verify.' },
    ],
  },
  {
    method: 'POST',
    path: '/api/users/:id/mfa/reset',
    title: 'Admin Reset User MFA',
    description:
      'Admin disable of ANOTHER user\'s MFA — used when the user lost their authenticator device. After reset, the target user must re-enroll before they can submit a disbursement again. Merchant admins can only reset users under the same merchantId.',
    category: 'MFA',
    scope: 'dashboard',
    permissions: ['users.mfa.reset'],
    userType: 'any',
    responseExample: { ok: true, userId: 42, mfaEnabled: false },
  },

  // ====== Wallet ======
  {
    method: 'GET',
    path: '/api/merchants/:id/wallet',
    title: 'Get Wallet Balance',
    description: 'Single-row read of merchant wallet: pending, active, reserved, lifetime totals.',
    category: 'Wallet',
    scope: 'dashboard',
    permissions: ['wallet.view', 'wallet.internal.view'],
    userType: 'any',
  },
  {
    method: 'GET',
    path: '/api/merchants/:id/wallet/ledger',
    title: 'Wallet Ledger History',
    description: 'Cursor-paginated history of every wallet movement.',
    category: 'Wallet',
    scope: 'dashboard',
    permissions: ['wallet.ledger.view', 'wallet.internal.view'],
    userType: 'any',
  },
  {
    method: 'POST',
    path: '/api/merchants/:id/wallet/adjustments',
    title: 'Manual Wallet Adjustment',
    description: 'Operator-initiated balance adjustment. Audited, requires a justification (≥10 chars).',
    category: 'Wallet',
    scope: 'dashboard',
    permissions: ['wallet.internal.adjust'],
    userType: 'internal-only',
  },

  // ====== Users ======
  {
    method: 'GET',
    path: '/api/users',
    title: 'List Users',
    description: 'List users. External callers are auto-scoped to their own merchant; internal callers can filter by scope/merchant.',
    category: 'Users',
    scope: 'dashboard',
    permissions: ['users.view', 'users.internal.view'],
    userType: 'any',
  },
  {
    method: 'GET',
    path: '/api/users/:id',
    title: 'Get User',
    description: 'Single user detail. Cross-merchant access blocked for external callers.',
    category: 'Users',
    scope: 'dashboard',
    permissions: ['users.view', 'users.internal.view'],
    userType: 'any',
  },
  {
    method: 'POST',
    path: '/api/users',
    title: 'Create User',
    description: 'External callers create users under their own merchant only. Internal callers may create internal or external users.',
    category: 'Users',
    scope: 'dashboard',
    permissions: ['users.manage', 'users.internal.manage'],
    userType: 'any',
  },
  {
    method: 'PATCH',
    path: '/api/users/:id',
    title: 'Update User',
    description: 'Change account name, role assignment, or password. Superusers cannot be modified via this endpoint.',
    category: 'Users',
    scope: 'dashboard',
    permissions: ['users.manage', 'users.internal.manage'],
    userType: 'any',
  },
  {
    method: 'DELETE',
    path: '/api/users/:id',
    title: 'Delete User',
    description: 'Hard delete. Refuses to delete superusers or yourself.',
    category: 'Users',
    scope: 'dashboard',
    permissions: ['users.manage', 'users.internal.manage'],
    userType: 'any',
  },

  // ====== IP Whitelist ======
  {
    method: 'GET',
    path: '/api/users/:id/ip-whitelist',
    title: 'List User IP Whitelist',
    description: 'Per-user login IP whitelist. Self always allowed to view own list.',
    category: 'IP Whitelist',
    scope: 'dashboard',
    permissions: ['ip_whitelist.user.view', 'ip_whitelist.internal.view'],
    userType: 'any',
  },
  {
    method: 'POST',
    path: '/api/users/:id/ip-whitelist',
    title: 'Add CIDR to User IP Whitelist',
    description: 'Add an allowed CIDR for this user\'s logins. Self always allowed to manage own list.',
    category: 'IP Whitelist',
    scope: 'dashboard',
    permissions: ['ip_whitelist.user.manage', 'ip_whitelist.internal.manage'],
    userType: 'any',
  },
  {
    method: 'DELETE',
    path: '/api/users/:id/ip-whitelist/:rowId',
    title: 'Remove User IP Whitelist Entry',
    description: 'Remove a CIDR. Warning: deleting the last entry locks the user out of logins.',
    category: 'IP Whitelist',
    scope: 'dashboard',
    permissions: ['ip_whitelist.user.manage', 'ip_whitelist.internal.manage'],
    userType: 'any',
  },
  {
    method: 'GET',
    path: '/api/merchants/:id/api-ip-whitelist',
    title: 'List Merchant API IP Whitelist',
    description: 'CIDRs allowed to call the server-to-server merchant API endpoints.',
    category: 'IP Whitelist',
    scope: 'dashboard',
    permissions: ['ip_whitelist.merchant.view', 'ip_whitelist.internal.view'],
    userType: 'any',
  },
  {
    method: 'POST',
    path: '/api/merchants/:id/api-ip-whitelist',
    title: 'Add CIDR to Merchant API Whitelist',
    description: 'Add a CIDR that the merchant\'s API calls may originate from.',
    category: 'IP Whitelist',
    scope: 'dashboard',
    permissions: ['ip_whitelist.merchant.manage', 'ip_whitelist.internal.manage'],
    userType: 'any',
  },
  {
    method: 'DELETE',
    path: '/api/merchants/:id/api-ip-whitelist/:rowId',
    title: 'Remove Merchant API Whitelist Entry',
    description: 'Remove a CIDR from the merchant\'s API whitelist.',
    category: 'IP Whitelist',
    scope: 'dashboard',
    permissions: ['ip_whitelist.merchant.manage', 'ip_whitelist.internal.manage'],
    userType: 'any',
  },

  // ====== Roles & Permissions ======
  {
    method: 'GET',
    path: '/api/roles',
    title: 'List Roles',
    description: 'List roles. External callers are pinned to scope=merchant; internal callers may filter by scope.',
    category: 'Roles & Permissions',
    scope: 'dashboard',
    permissions: [],
    userType: 'any',
  },
  {
    method: 'GET',
    path: '/api/roles/:id',
    title: 'Get Role',
    description: 'Single role detail (id, slug, name, scope, system flag, sortOrder, userCount).',
    category: 'Roles & Permissions',
    scope: 'dashboard',
    permissions: [],
    userType: 'any',
  },
  {
    method: 'GET',
    path: '/api/roles/:id/permissions',
    title: 'Get Role Permissions',
    description: 'List of permission keys currently granted to this role.',
    category: 'Roles & Permissions',
    scope: 'dashboard',
    permissions: [],
    userType: 'any',
  },
  {
    method: 'PATCH',
    path: '/api/roles/:id/permissions',
    title: 'Replace Role Permission Matrix',
    description: 'Set the full permission key set for this role. Diffs against current state and inserts/deletes accordingly. Invalidates the per-user permission cache for every assignee.',
    category: 'Roles & Permissions',
    scope: 'dashboard',
    permissions: ['roles.merchant.manage', 'roles.internal.manage'],
    userType: 'internal-only',
  },
  {
    method: 'POST',
    path: '/api/roles',
    title: 'Create Custom Role',
    description: 'Create a non-system role. Slug must be lower_snake_case.',
    category: 'Roles & Permissions',
    scope: 'dashboard',
    permissions: ['roles.merchant.manage', 'roles.internal.manage'],
    userType: 'internal-only',
  },
  {
    method: 'PATCH',
    path: '/api/roles/:id',
    title: 'Rename / Re-describe Role',
    description: 'Rename or update description. Slug, scope, and system flag are immutable.',
    category: 'Roles & Permissions',
    scope: 'dashboard',
    permissions: ['roles.merchant.manage', 'roles.internal.manage'],
    userType: 'internal-only',
  },
  {
    method: 'DELETE',
    path: '/api/roles/:id',
    title: 'Delete Custom Role',
    description: 'Delete a non-system role. Refuses if any users still have this role assigned.',
    category: 'Roles & Permissions',
    scope: 'dashboard',
    permissions: ['roles.merchant.manage', 'roles.internal.manage'],
    userType: 'internal-only',
  },
  {
    method: 'GET',
    path: '/api/permissions',
    title: 'List Permission Catalog',
    description: 'Full catalog of permission keys (read by the Roles & Permissions matrix editor).',
    category: 'Roles & Permissions',
    scope: 'dashboard',
    permissions: ['roles.merchant.manage', 'roles.internal.manage'],
    userType: 'internal-only',
  },

  // ====== Invites ======
  {
    method: 'POST',
    path: '/api/invites',
    title: 'Create Invite',
    description: 'Generate a one-time invite token. External callers create invites for their own merchant only.',
    category: 'Invites',
    scope: 'dashboard',
    permissions: ['users.manage', 'users.internal.manage'],
    userType: 'any',
  },
  {
    method: 'GET',
    path: '/api/invites',
    title: 'List Pending Invites',
    description: 'List unused, non-expired invites. External callers see only their own merchant\'s.',
    category: 'Invites',
    scope: 'dashboard',
    permissions: ['users.view', 'users.internal.view'],
    userType: 'any',
  },
  {
    method: 'DELETE',
    path: '/api/invites/:id',
    title: 'Revoke Invite',
    description: 'Revoke a pending invite. Refuses if already accepted.',
    category: 'Invites',
    scope: 'dashboard',
    permissions: ['users.manage', 'users.internal.manage'],
    userType: 'any',
  },
];

/** Filter the catalog to what the current viewer is allowed to call. */
export function visibleEndpoints(
  endpoints: readonly ApiEndpoint[],
  ctx: {
    isSuperuser: boolean;
    userType: 'internal' | 'external';
    has: (key: string) => boolean;
  },
): ApiEndpoint[] {
  return endpoints.filter((ep) => {
    if (ctx.isSuperuser) return true;
    if (ep.userType === 'internal-only' && ctx.userType !== 'internal') return false;
    if (ep.userType === 'external-only' && ctx.userType !== 'external') return false;
    // Server-to-server endpoints are integration-facing — show to anyone with
    // credentials-view (they're the one who'd actually wire it up).
    if (ep.scope === 'merchant-api') {
      return ctx.has('account.credentials.view') || ctx.userType === 'internal';
    }
    if (ep.permissions.length === 0) return true;
    return ep.permissions.some((p) => ctx.has(p));
  });
}
